import React, { useState, useEffect, useMemo } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useSnackbar } from 'v3/components/Snackbar';
import { validatePermission } from 'actions/index';
import { CNPJ_MASK, CPF_MASK, PHONE_MASK } from 'v3/utils/masks';

import Input from 'v3/components/Input';
import InputCurrency from 'v3/components/Input/Currency';
import Text from 'v3/components/Text';
import Radio from 'v3/components/Radio';
import Select from 'v3/components/Select';
import api from 'services/api';
import { FaInfoCircle } from 'react-icons/fa';
import { useClient } from '../context';

const RadioGroupWrapper = styled.div`
  .radio-error input + div[type='radio'] {
    border-color: #f64e60;
  }
  .radio-error input:checked + div[type='radio'] {
    background-color: #f64e60;
    border: none;
  }
`;

export default function ClientData() {
  const client = useClient();
  const params = useParams();
  const snackbar = useSnackbar();

  const requiredMessage = 'Esse campo é obrigatório';
  function isEmptyValue(value) {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim().length === 0;
    if (Array.isArray(value)) return value.length === 0;
    return false;
  }

  function requiredError(value, existingError) {
    if (!client.showRequiredErrors) return existingError || null;
    return existingError || (isEmptyValue(value) ? requiredMessage : null);
  }

  function handleDocumentChange(event) {
    const value = event.target.value;
    const shouldUppercase =
      !client.data.is_international && client?.data?.personType?.name === 'Jurídica';

    client.setData({ cgccpf: shouldUppercase ? value.toUpperCase() : value });
  }

  const [tagRegisterPermitted, setTagRegisterPermitted] = useState(
    validatePermission('CRIAR_TAGS'),
  );
  const [takerRoles, setTakerRoles] = useState([]);

  useEffect(() => {
    if (!params.id)
      client.setData({
        personType: { name: 'Jurídica' },
        is_international: false,
        is_rewards_member: false,
        cgccpf: '',
        ie: '',
        social_number: '',
        social_name: '',
        fantasy_name: '',
        phone: '',
        email: '',
        takerRole: null,
        clientTags: [],
        credit_limit: null,
      });
    else if (client.data.is_international === null) client.setData({ is_international: false });
  }, [params.id]);

  async function handleTagCreate(value) {
    let tagValue = value;

    if (tagRegisterPermitted) {
      if (tagValue) {
        tagValue = tagValue.trim();
        if (tagValue.length >= 3) {
          try {
            const response = await api.post('tags', { name: tagValue });
            if (response.status === 200) {
              const newTag = {
                id: response.data.id,
                name: tagValue,
              };
              if (client.data.tags !== null)
                client.setData({ tags: [...client.data.tags, newTag] });
              else {
                client.setData({ tags: [newTag] });
              }
            }
          } catch (err) {
            // Handle exception
          }
        }
      }
    } else {
      snackbar.show(<Text>Usuário sem permissão para criar tags</Text>, {
        type: 'error',
      });
    }
  }

  const documentInput = useMemo(() => {
    if (client.data.is_international) return { label: 'Documento *', mask: false };
    if (client.data.is_international) {
      return {
        label: 'Documento *',
        mask: false
      };
    }
    if (client?.data?.personType?.name === 'Jurídica') {
      return {
        label: 'CNPJ *',
        mask: CNPJ_MASK,
      };
    }
    return {
      label: 'CPF *',
      mask: CPF_MASK,
    };
  }, [client?.data?.personType, client?.data?.is_international]);

  useEffect(() => {
    async function getTakerRoles() {
      const response = await api.get('taker-roles');
      const roles = response.data.map((role) => ({
        name: role.name,
        code: role.id,
      }));
      setTakerRoles(roles);
    }
    getTakerRoles();
  }, []);

  useEffect(() => {
    if (client?.data?.personType?.name === 'Física') {
      client.setData({ ie: null });
    }
  }, [client.data.personType]);

  async function fetchSelectOptions(url, params = {}) {
    try {
      const { data } = await api.get(url, { params });

      return data;
    } catch (err) {
      return [];
    }
  }

  return (
    <Row className="form">
      <Col xs={12} md={6}>
        <RadioGroupWrapper>
          <div
            className={
              client.showRequiredErrors && isEmptyValue(client.data?.personType?.name)
                ? 'radio-error'
                : undefined
            }
          >
            <Radio.Group
              label="Tipo de pessoa *"
              onChange={({ target }) => {
                client.setData({
                  personType: { ...client.data.personType, name: target.value },
                  cgccpf: '',
                });
              }}
              value={client.data?.personType?.name}
              horizontal
            >
              <Radio value="Jurídica">
                <Text color="dark" type="label">
                  Jurídica
                </Text>
              </Radio>
              <Radio value="Física">
                <Text color="dark" type="label">
                  Física
                </Text>
              </Radio>
            </Radio.Group>
          </div>
          {client.showRequiredErrors && isEmptyValue(client.data?.personType?.name) && (
            <Text type="little" color="error" style={{ marginTop: 6, display: 'block' }}>
              {requiredMessage}
            </Text>
          )}
        </RadioGroupWrapper>
      </Col>

      <Col xs={12} md={6}>
        <Radio.Group
          label="Estrangeiro"
          onChange={({ target }) => {
            client.setData({
              is_international: target.value,
            });
          }}
          value={client.data?.is_international}
          horizontal
        >
          <Radio value>
            <Text color="dark" type="label">
              Sim
            </Text>
          </Radio>
          <Radio value={false}>
            <Text color="dark" type="label">
              Não
            </Text>
          </Radio>
        </Radio.Group>
      </Col>

      <Col xs={12}>
        <Radio.Group
          label="Estabelecimento"
          onChange={({ target }) => {
            client.setData({
              is_rewards_member: target.value,
            });
          }}
          value={client.data?.is_rewards_member}
          horizontal
        >
          <Radio value>
            <Text color="dark" type="label">
              Sim
            </Text>
          </Radio>
          <Radio value={false}>
            <Text color="dark" type="label">
              Não
            </Text>
          </Radio>
        </Radio.Group>
      </Col>

      <Col xs={12} md={6}>
        <Input
          label={documentInput.label}
          value={client?.data?.cgccpf}
          onChange={handleDocumentChange}
          masked
          mask={documentInput.mask}
          error={requiredError(client?.data?.cgccpf, client.errors['data.cgccpf'])}
          guide={false}
        />
      </Col>
      {client?.data?.personType?.name === 'Jurídica' && (
        <Col xs={12} md={6}>
          <Input
            label={
              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <FaInfoCircle title="Caso não possua colocar como ISENTO" size={14} />
                Inscrição estadual *
              </div>
            }
            value={client?.data?.ie}
            onChange={(e) => client.setData({ ie: e.target.value })}
            error={requiredError(client?.data?.ie, client.errors['data.ie'])}
            placeholder="ISENTO"
          />
        </Col>
      )}
      <Col xs={12}>
        <Input
          label={client?.data?.personType?.name === 'Jurídica' ? 'Razão social *' : 'Nome *'}
          value={client?.data?.social_name}
          onChange={(e) => client.setData({ social_name: e.target.value })}
          error={requiredError(client?.data?.social_name, client.errors['data.social_name'])}
        />
      </Col>
      {client?.data?.personType?.name === 'Jurídica' && (
        <Col xs={12} md={12}>
          <Input
            label="Nome fantasia *"
            value={client?.data?.fantasy_name}
            onChange={(e) => client.setData({ fantasy_name: e.target.value })}
            error={requiredError(client?.data?.fantasy_name, client.errors['data.fantasy_name'])}
          />
        </Col>
      )}
      <Col xs={12} md={6}>
        <Input
          label="Telefone *"
          value={client.data?.phone}
          onChange={(e) => client.setData({ phone: e.target.value })}
          masked
          mask={!client.data.is_international ? PHONE_MASK : false}
          error={requiredError(client.data?.phone, client.errors['data.phone'])}
          guide={false}
        />
      </Col>
      <Col xs={12} md={12}>
        <Input
          label="E-mail *"
          value={client?.data?.email}
          onChange={(e) => client.setData({ email: e.target.value })}
          error={requiredError(client?.data?.email, client.errors['data.email'])}
        />
      </Col>
      <Col xs={12} className="mb-3">
        <Select
          label="Contribuinte ICMS *"
          horizontal
          value={client.data?.takerRole}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.code}
          onChange={(event) => client.setData({ takerRole: event })}
          options={takerRoles}
          error={requiredError(
            client.data?.takerRole,
            client.errors['data.takerRole.name'] || client.errors['data.takerRole'],
          )}
        />
      </Col>
      <Col xs={12} className="mb-3">
        <Select.Async
          label="Tags"
          creatable
          onCreateOption={handleTagCreate}
          multiple
          value={client.data?.clientTags}
          horizontal
          modalHeading="Adicione uma tag"
          modalBodyTitle="Tags:"
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.id}
          onChange={(event) => client.setData({ clientTags: event })}
          onSearch={(search) =>
            fetchSelectOptions('tags', {
              search,
              not_paginated: true,
            })
          }
        />
      </Col>
      <Col xs={12}>
        <InputCurrency
          label="Limite de Crédito Disponível"
          value={client.data?.credit_limit}
          onChange={(_, value) => client.setData({ credit_limit: value })}
          placeholder="R$"
        />
      </Col>
    </Row>
  );
}
